/**
 * Lightweight token-level diff used by the AI review mode.
 *
 * Tokenizes HTML into tags / words / whitespace and computes a longest common
 * subsequence so changes can be rendered inline (deletions struck, insertions
 * highlighted). Self-contained — no external diff dependency.
 */

export function tokenize(html) {
    return html.match(/<[^>]+>|[^<>\s]+|\s+/g) || [];
}

export const isTag = (token) => /^<[^>]+>$/.test(token);
export const isWhitespace = (token) => /^\s+$/.test(token);

/**
 * Diff two token arrays via an LCS table, returning an ordered list of
 * { type: 'eq' | 'del' | 'ins', token } operations.
 */
export function diffTokens(a, b) {
    const n = a.length;
    const m = b.length;
    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));

    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            dp[i][j] =
                a[i] === b[j]
                    ? dp[i + 1][j + 1] + 1
                    : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }

    const ops = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) {
            ops.push({ type: "eq", token: a[i] });
            i++;
            j++;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            ops.push({ type: "del", token: a[i] });
            i++;
        } else {
            ops.push({ type: "ins", token: b[j] });
            j++;
        }
    }
    while (i < n) {
        ops.push({ type: "del", token: a[i++] });
    }
    while (j < m) {
        ops.push({ type: "ins", token: b[j++] });
    }
    return ops;
}
